import { defineStore } from 'pinia'
import { ref } from 'vue'
import type { Ref } from 'vue'
import axiosClient from '@/api/axiosClient'
import { useGlobalStore } from '@/store/storeGlobal'
import { useNotificationsStore } from '@/store/notificationsStore'
import { useRefreshStore } from '@/store/Setup/refreshStore'
import { useMenuStore } from '@/store/storeMenu'
import type { components } from '@/api/types/auto-generated'
import {
  fetchDatabaseObjectsByTableDefinitionId as fetchByTableDefId,
  deleteDatabaseObject as deleteDatabaseObjectApi,
  updateDatabaseObject as updateDatabaseObjectApi,
  setDefaultDatabaseObject,
  toggleDatabaseObjectActive,
  createView as createViewApi,
  addExistingView as addExistingViewApi,
  saveViewDefinition,
  fetchViewsForSchema as fetchViewsForSchemaApi,
  recreateViews,
  updateViewsSafely as updateViewsSafelyApi,
  checkViewsCompleteness as checkViewsCompletenessApi,
  addMissingColumns as addMissingColumnsApi,
  updateDatabaseObjectsOrder,
  type DatabaseObjectEntity,
  type UpdateDatabaseObjectRequest,
  type CreateViewRequest,
  type ViewDefinitionRequest,
  type ViewUpdateResult
} from '@/api/endpoints/databaseObjects'
import { getWorklistDetails, type WorklistDetails } from '@/api/endpoints/worklistDetails'
import { batchUpdateDatabaseObjectColumns, reorderDatabaseObjectColumns, type BatchColumnUpdate } from '@/api/endpoints/databaseObjectColumns'
import { useTableDefinitionsStore } from '@/store/tableDefinitionsStore'
import { useSystemMetadataStore } from '@/store/systemMetadataStore'
import { useVerdilisteStore } from '@/store/storeVerdiliste'
import type {
  UpdateDatabaseObjectParams,
  CreateWorkListsParams,
  UpdateViewsSafelyParams,
  CheckViewsCompletenessParams,
  AddMissingColumnsParams,
  RebuildViewParams
} from './types/databaseObjectStore.types'

type DatabaseObjectColumnUpdateRequest = components['schemas']['DatabaseObjectColumnUpdateRequest']

export const useDatabaseObjectStore = defineStore('databaseObjectStore', () => {
  const globalStore = useGlobalStore()
  const notificationsStore = useNotificationsStore()
  const refreshStore = useRefreshStore()
  const menuStore = useMenuStore()

  const debugPrefix = '[DatabaseObject]'

  const wsItem: Ref<WorklistDetails | null> = ref(null)
  const databaseObjects: Ref<DatabaseObjectEntity[] | null> = ref(null)
  const isLoadingDatabaseObject: Ref<boolean> = ref(false)
  const selectedDatabaseObjectId: Ref<number | null> = ref(null)

  function clearDatabaseObject (): void {
    wsItem.value = null
    databaseObjects.value = null
    selectedDatabaseObjectId.value = null
    isLoadingDatabaseObject.value = false
  }

  async function fetchDatabaseObjectsByTableDefinitionId (tableDefinitionId: number): Promise<DatabaseObjectEntity[]> {
    globalStore.debug(`${debugPrefix} fetchDatabaseObjectsByTableDefinitionId(id)`, tableDefinitionId)
    isLoadingDatabaseObject.value = true
    try {
      const data = await fetchByTableDefId(tableDefinitionId)
      // Immutable sort - opprett kopi før sortering
      const sortedData = [...data].sort((a: any, b: any) => ((a.ordinal as number) || 0) - ((b.ordinal as number) || 0))
      databaseObjects.value = sortedData
      globalStore.debug(`${debugPrefix} fetchDatabaseObjectsByTableDefinitionId`, databaseObjects)
      isLoadingDatabaseObject.value = false
      return sortedData
    } catch (error) {
      globalStore.debug(`${debugPrefix} fetchDatabaseObjectsByTableDefinitionId error:`, error)
      isLoadingDatabaseObject.value = false
      throw error
    }
  }

  async function fetchDatabaseObjectById (databaseObjectId: number): Promise<void> {
    if (!databaseObjectId) {
      return
    }
    selectedDatabaseObjectId.value = databaseObjectId
    await fetchDatabaseObject()
  }

  async function fetchDatabaseObject (): Promise<void> {
    if (!selectedDatabaseObjectId.value) {
      notificationsStore.addError('Det er ikke valgt arbeidsliste, da kan den ikke hentes!')
      return
    }
    wsItem.value = null
    isLoadingDatabaseObject.value = true
    try {
      const data = await getWorklistDetails(selectedDatabaseObjectId.value)
      if (data) {
        wsItem.value = data
        isLoadingDatabaseObject.value = false
        return
      }
      notificationsStore.addError('fetchDatabaseObject failed: ', 'Ingen data funnet')
    } catch (error) {
      globalStore.debug(`${debugPrefix} fetchDatabaseObject error:`, error)
      notificationsStore.addError('fetchDatabaseObject failed: ', 'Feil ved henting av arbeidsliste')
    } finally {
      isLoadingDatabaseObject.value = false
    }
  }

  async function updateDatabaseObject (params: UpdateDatabaseObjectParams): Promise<void> {
    const updateObject: UpdateDatabaseObjectRequest = {
      systemObjectId: params.systemObjectId,
      databaseObjectId: params.databaseObjectId,
      dbSchemaName: params.dbSchemaName,
      dbViewName: params.dbViewName,
      itemName: params.itemName,
      active: params.active,
      default: params.isDefault
    }
    await updateDatabaseObjectApi(updateObject)
    notificationsStore.addSuccess('Arbeidslisten ble lagret')
  }

  /**
   * Core-funksjon for å oppdatere en kolonne (uten notifikasjoner eller auto-refresh).
   * Brukes for bulk-operasjoner der man ønsker å vise én samlet melding.
   */
  async function updateDatabaseObjectColumnCore (dto: DatabaseObjectColumnUpdateRequest): Promise<void> {
    if (!dto.databaseObjectId || !dto.columnName) {
      throw new Error('databaseObjectId og columnName er påkrevd')
    }

    globalStore.debug(`${debugPrefix} updateDatabaseObjectColumnCore:`, dto)
    await axiosClient.put('v2/database/object/column', dto)
  }

  /**
   * Oppdaterer en kolonne med notifikasjon og auto-refresh.
   * Forventer DatabaseObjectColumnUpdateRequest DTO som input.
   * Bruk columnMappers (setupColumnToDTO eller queryColumnToDTO) for å lage DTO.
   */
  async function updateDatabaseObjectColumn (dto: DatabaseObjectColumnUpdateRequest): Promise<void> {
    await updateDatabaseObjectColumnCore(dto)
    notificationsStore.addSuccess(`Kolonne ${dto.columnName} er lagret`)

    // Auto-refresh hvis wsItem matcher
    if (wsItem.value?.databaseObjectId === dto.databaseObjectId && dto.databaseObjectId) {
      try {
        const worklistDetails = await getWorklistDetails(dto.databaseObjectId)
        wsItem.value = worklistDetails
      } catch (error) {
        globalStore.debug(`${debugPrefix} refresh error:`, error)
      }
    }
  }

  /**
   * Batch-oppdaterer kolonner med notifikasjon og auto-refresh.
   * Bruker backend batch-endpoint for effektiv og transaksjonelt sikker oppdatering.
   */
  async function batchUpdateColumns (databaseObjectId: number, updates: BatchColumnUpdate[]): Promise<void> {
    if (!databaseObjectId || updates.length === 0) {
      throw new Error('databaseObjectId og updates er påkrevd')
    }

    globalStore.debug(`${debugPrefix} batchUpdateColumns:`, { databaseObjectId, count: updates.length })

    try {
      await batchUpdateDatabaseObjectColumns(databaseObjectId, updates)

      const uniqueColumnCount = new Set(updates.map(u => u.columnName)).size
      if (uniqueColumnCount < 10) {
        const columnNames = updates
          .slice(0, 5)
          .map(u => u.columnName)
          .join(', ')
        notificationsStore.addSuccess(
          `Lagret endringer for ${uniqueColumnCount} kolonner: ${columnNames}${uniqueColumnCount > 5 ? '...' : ''}`
        )
      } else {
        notificationsStore.addSuccess(`Lagret endringer for ${uniqueColumnCount} kolonner`)
      }

      // Auto-refresh hvis wsItem matcher
      if (wsItem.value?.databaseObjectId === databaseObjectId) {
        try {
          const worklistDetails = await getWorklistDetails(databaseObjectId)
          wsItem.value = worklistDetails
        } catch (error) {
          globalStore.debug(`${debugPrefix} refresh error:`, error)
        }
      }
    } catch (error: any) {
      globalStore.debug(`${debugPrefix} batchUpdateColumns error:`, error)
      notificationsStore.addError('Feil ved lagring av kolonneinnstillinger', error.message)
      throw error
    }
  }

  async function setDefaultWorklist (databaseObjectId: number): Promise<boolean> {
    try {
      await setDefaultDatabaseObject(databaseObjectId)
      notificationsStore.addSuccess('Endret til primær')
      refreshStore.triggerRefresh()
      return true
    } catch (error) {
      globalStore.debug(`${debugPrefix} setDefaultWorklist error:`, error)
      notificationsStore.addError('Kunne ikke sette som primær')
      throw error
    }
  }

  async function toggleActive (): Promise<void> {
    if (!selectedDatabaseObjectId.value || !wsItem.value) {
      throw new Error('Ingen arbeidsliste valgt')
    }

    const databaseObjectId = selectedDatabaseObjectId.value
    const active = !wsItem.value.active

    try {
      await toggleDatabaseObjectActive(databaseObjectId, active)
      wsItem.value.active = active
      const message = active ? 'Arbeidslisten er aktivert' : 'Arbeidslisten er deaktivert'
      notificationsStore.addSuccess(message)
    } catch (error) {
      globalStore.debug(`${debugPrefix} toggleActive error:`, error)
      notificationsStore.addError('Kunne ikke endre status')
      throw error
    }
  }

  async function createView (
    systemObjectId: number,
    displayName: string,
    dbSchemaName: string,
    dbViewName: string
  ): Promise<void> {
    globalStore.debug(`${debugPrefix} createView`, systemObjectId, displayName, dbSchemaName, dbViewName)

    const request: CreateViewRequest = {
      systemObjectId,
      itemName: displayName,
      dbSchemaName,
      dbViewName
    }

    try {
      await createViewApi(request)
      globalStore.debug(`${debugPrefix} createView success`)
      notificationsStore.addSuccess('View er opprettet i databasen')
      refreshStore.triggerRefresh()
    } catch (error) {
      globalStore.debug(`${debugPrefix} createView error:`, error)
      notificationsStore.addError('Kunne ikke opprette view')
      throw error
    }
  }

  async function addExistingView (
    systemObjectId: number,
    displayName: string,
    dbSchemaName: string,
    dbViewName: string
  ): Promise<void> {
    globalStore.debug(`${debugPrefix} addExistingView`, systemObjectId, displayName, dbSchemaName, dbViewName)

    const request: CreateViewRequest = {
      systemObjectId,
      itemName: displayName,
      dbSchemaName,
      dbViewName
    }

    try {
      await addExistingViewApi(request)
      notificationsStore.addSuccess('View er opprettet i databasen')
      refreshStore.triggerRefresh()
    } catch (error) {
      globalStore.debug(`${debugPrefix} addExistingView error:`, error)
      notificationsStore.addError('Kunne ikke legge til view')
      throw error
    }
  }

  async function saveView (refreshViewOnly: boolean): Promise<void> {
    if (!selectedDatabaseObjectId.value) {
      throw new Error('Ingen arbeidsliste valgt')
    }

    const viewDefinitionRequest: ViewDefinitionRequest = {
      databaseObjectId: selectedDatabaseObjectId.value,
      refreshViewOnly
    }

    if (!refreshViewOnly && wsItem.value?.sql) {
      viewDefinitionRequest.sql = wsItem.value.sql
    }

    try {
      const data = await saveViewDefinition(viewDefinitionRequest)
      if (refreshViewOnly) {
        notificationsStore.addSuccess('Endringer er forkastet')
      } else {
        notificationsStore.addSuccess('View er lagret i databasen')
      }
      if (data) {
        wsItem.value = data
      }
    } catch (error) {
      globalStore.debug(`${debugPrefix} saveView error:`, error)
      notificationsStore.addError('Kunne ikke lagre view')
      throw error
    }
  }

  const refreshView = () => saveView(true)
  const updateView = () => saveView(false)

  async function deleteView (): Promise<void> {
    if (!selectedDatabaseObjectId.value) {
      throw new Error('Ingen arbeidsliste valgt')
    }

    try {
      await deleteDatabaseObjectApi(selectedDatabaseObjectId.value)
      if (wsItem.value) {
        wsItem.value.viewExists = false
      }
      notificationsStore.addSuccess('View slettet i basen')
    } catch (error) {
      globalStore.debug(`${debugPrefix} deleteView error:`, error)
      notificationsStore.addError('Kunne ikke slette view')
      throw error
    }
  }

  async function deleteDatabaseObjectAction (databaseObjectId: number): Promise<boolean> {
    globalStore.debug(`${debugPrefix} deleteDatabaseObjectAction`, databaseObjectId)
    try {
      await deleteDatabaseObjectApi(databaseObjectId)
      notificationsStore.addSuccess('Arbeidsliste slettet')
      refreshStore.triggerRefresh()
      return true
    } catch (error) {
      globalStore.debug(`${debugPrefix} deleteDatabaseObjectAction error:`, error)
      notificationsStore.addError('Kunne ikke slette arbeidsliste')
      return false
    }
  }

  async function fetchViewsForSchema (searchString: string): Promise<any[]> {
    try {
      return await fetchViewsForSchemaApi(searchString)
    } catch (error) {
      globalStore.debug(`${debugPrefix} fetchViewsForSchema error:`, error)
      notificationsStore.addError('Kunne ikke hente views')
      throw error
    }
  }

  async function createWorkLists (params: CreateWorkListsParams): Promise<void> {
    globalStore.debug(`${debugPrefix} createWorkLists`, params.systemObjectId)

    // Hent system object for å få tableDefinitionId
    const systemObject = await menuStore.getSystemObject(params.systemObjectId)
    if (!systemObject?.tableDefinitionId) {
      throw new Error(`System object ${params.systemObjectId} har ingen tilknyttet tabell`)
    }

    try {
      await recreateViews(systemObject.tableDefinitionId)
      refreshStore.triggerRefresh()
    } catch (error) {
      globalStore.debug(`${debugPrefix} createWorkLists error:`, error)
      notificationsStore.addError('Kunne ikke opprette arbeidslister')
      throw error
    }
  }

  async function updateViewsSafely (params: UpdateViewsSafelyParams): Promise<ViewUpdateResult | undefined> {
    globalStore.debug(`${debugPrefix} updateViewsSafely`, params.tableDefinitionId, params.forceOverride)

    try {
      const result = await updateViewsSafelyApi(params.tableDefinitionId, params.forceOverride ?? false)
      if (result?.success) {
        refreshStore.triggerRefresh()
      }
      return result
    } catch (error) {
      globalStore.debug(`${debugPrefix} updateViewsSafely error:`, error)
      notificationsStore.addError('Kunne ikke oppdatere views')
      throw error
    }
  }

  async function checkViewsCompleteness (params: CheckViewsCompletenessParams): Promise<ViewUpdateResult> {
    globalStore.debug(`${debugPrefix} checkViewsCompleteness`, params.tableDefinitionId)

    try {
      return await checkViewsCompletenessApi(params.tableDefinitionId)
    } catch (error) {
      globalStore.debug(`${debugPrefix} checkViewsCompleteness error:`, error)
      notificationsStore.addError('Kunne ikke sjekke views')
      throw error
    }
  }

  async function addMissingColumns (params: AddMissingColumnsParams): Promise<ViewUpdateResult> {
    globalStore.debug(`${debugPrefix} addMissingColumns`, params.tableDefinitionId)

    try {
      const result = await addMissingColumnsApi(params.tableDefinitionId)
      if (result?.success) {
        refreshStore.triggerRefresh()
      }
      return result
    } catch (error) {
      globalStore.debug(`${debugPrefix} addMissingColumns error:`, error)
      notificationsStore.addError('Kunne ikke legge til manglende kolonner')
      throw error
    }
  }

  async function rebuildView (params: RebuildViewParams): Promise<ViewUpdateResult> {
    globalStore.debug(`${debugPrefix} rebuildView`, params.tableDefinitionId)

    try {
      await recreateViews(params.tableDefinitionId)
      refreshStore.triggerRefresh()
      return { success: true, message: 'Views gjenopprettet' }
    } catch (error) {
      globalStore.debug(`${debugPrefix} rebuildView error:`, error)
      notificationsStore.addError('Kunne ikke gjenopprette views')
      throw error
    }
  }

  async function updateOrder (newItems: DatabaseObjectEntity[]): Promise<void> {
    databaseObjects.value = newItems
    try {
      await updateDatabaseObjectsOrder(newItems)
    } catch (error) {
      globalStore.debug(`${debugPrefix} updateOrder error:`, error)
      notificationsStore.addError('Kunne ikke oppdatere rekkefølge')
      throw error
    }
  }

  async function loadWorklistDetails (databaseObjectId: number, signal?: AbortSignal): Promise<WorklistDetails | null> {
    if (!databaseObjectId) {
      return null
    }

    isLoadingDatabaseObject.value = true
    try {
      // Hent worklist detaljer
      const details = await getWorklistDetails(databaseObjectId, signal)

      // Prefetch relaterte data
      const tableDefinitionsStore = useTableDefinitionsStore()
      const systemMetadataStore = useSystemMetadataStore()
      const verdilisteStore = useVerdilisteStore()

      await Promise.all([
        details.systemObjectId ? tableDefinitionsStore.fetchBySystemObjectId(details.systemObjectId) : Promise.resolve(),
        systemMetadataStore.fetchEditComponents(),
        verdilisteStore.verdiliste_GetAll()
      ])

      return details
    } catch (err: any) {
      if (err.name === 'AbortError') throw err
      notificationsStore.addError('Kunne ikke hente detaljer for valgt arbeidsliste.')
      throw err
    } finally {
      isLoadingDatabaseObject.value = false
    }
  }

  async function reorderColumns (databaseObjectId: number, columns: any[]): Promise<void> {
    globalStore.debug(`${debugPrefix} reorderColumns`, { databaseObjectId, columns })
    try {
      await reorderDatabaseObjectColumns(databaseObjectId, columns)
      notificationsStore.addSuccess('Kolonner rekkefølge oppdatert')

      // Refresh arbeidsliste-data etter reordering
      if (wsItem.value?.databaseObjectId === databaseObjectId) {
        const worklistDetails = await getWorklistDetails(databaseObjectId)
        wsItem.value = worklistDetails
      }
    } catch (error: any) {
      globalStore.debug(`${debugPrefix} reorderColumns error:`, error)
      notificationsStore.addError('Feil ved reordering av kolonner', error.message)
      throw error
    }
  }

  return {
    wsItem,
    databaseObjects,
    isLoadingDatabaseObject,
    selectedDatabaseObjectId,
    clearDatabaseObject,
    fetchDatabaseObjectsByTableDefinitionId,
    fetchDatabaseObject,
    fetchDatabaseObjectById,
    updateDatabaseObject,
    updateDatabaseObjectColumn,
    updateDatabaseObjectColumnCore,
    batchUpdateColumns,
    setDefaultWorklist,
    toggleActive,
    refreshView,
    updateView,
    deleteView,
    deleteDatabaseObject: deleteDatabaseObjectAction,
    createView,
    addExistingView,
    fetchViewsForSchema,
    createWorkLists,
    updateViewsSafely,
    checkViewsCompleteness,
    addMissingColumns,
    rebuildView,
    updateOrder,
    loadWorklistDetails,
    reorderColumns
  }
})

